/**
 * Administration des adhésions
 * - Liste avec filtres (en attente / approuvés / refusés)
 * - Actions : approuver, refuser, éditer, supprimer, renvoyer email
 * - Création directe par admin (avec numéro forcé possible pour les 30 réservés)
 * - Attribution manuelle d'un numéro réservé FURDC-00001 → FURDC-00030
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, X, Save, Loader2, Check, XCircle, Mail,
  Search as SearchIcon, Hash, User, Users, Send,
  ClipboardList, CheckCircle2, AlertCircle, Filter, RefreshCw,
} from "lucide-react";
import api from "../../lib/api";

const STATUS_LABELS = {
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  approved: { label: "Approuvée", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  rejected: { label: "Refusée", cls: "bg-crimson/10 text-crimson border-crimson/20" },
};

const ENGAGEMENT = {
  adherent: "Adhérent",
  benevole: "Bénévole",
  militant: "Militant",
  sympathisant: "Sympathisant",
};

const ADMIN_DEFAULTS = {
  first_name: "", last_name: "", email: "", phone: "",
  country: "", city: "", profession: "",
  engagement_type: "adherent", motivation: "Adhésion manuelle par l'administration.",
  source: "", adhesion_number: "", auto_approve: true,
};

const fmt = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; } };

export default function AdhesionsAdmin() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(null); // pour attribution manuelle d'un numéro réservé
  const [rejecting, setRejecting] = useState(null);

  const load = async () => {
    const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
    const [{ data: adh }, { data: st }] = await Promise.all([
      api.get(`/admin/adhesions${params}`),
      api.get("/admin/adhesions/stats"),
    ]);
    setItems(adh); setStats(st);
  };
  useEffect(() => { load(); }, [statusFilter]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((a) =>
      (a.first_name || "").toLowerCase().includes(s) ||
      (a.last_name || "").toLowerCase().includes(s) ||
      (a.email || "").toLowerCase().includes(s) ||
      (a.adhesion_number || "").toLowerCase().includes(s) ||
      (a.country || "").toLowerCase().includes(s) ||
      (a.city || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const approve = async (id) => {
    if (!confirm("Approuver cette adhésion ? Un numéro FURDC-XXXXX sera attribué et un email envoyé.")) return;
    try {
      const r = await api.post(`/admin/adhesions/${id}/approve`);
      toast.success(`Approuvée · ${r.data.adhesion_number}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const doDelete = async (id) => {
    if (!confirm("Supprimer définitivement cette adhésion ?")) return;
    await api.delete(`/admin/adhesions/${id}`); load();
  };
  const resendEmail = async (id) => {
    const r = await api.post(`/admin/adhesions/${id}/resend-email`);
    if (r.data.ok) toast.success("Email renvoyé");
    else toast.error("Envoi échoué");
  };

  return (
    <div data-testid="adhesions-admin">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <StatCard n={stats.total} l="Total" Icon={ClipboardList} />
          <StatCard n={stats.pending} l="En attente" Icon={AlertCircle} tone="amber" />
          <StatCard n={stats.approved} l="Approuvées" Icon={CheckCircle2} tone="emerald" />
          <StatCard n={stats.rejected} l="Refusées" Icon={XCircle} tone="crimson" />
          <StatCard n={`${stats.reserved_used}/30`} l="Réservés utilisés" Icon={Hash} tone="ink" />
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">Rechercher</label>
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, email, numéro, ville…"
                   className="inp pl-9" data-testid="adh-admin-search" />
          </div>
        </div>
        <div>
          <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">Statut</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="inp bg-white" data-testid="adh-admin-status">
            <option value="all">Tous</option>
            <option value="pending">En attente</option>
            <option value="approved">Approuvées</option>
            <option value="rejected">Refusées</option>
          </select>
        </div>
        <button onClick={load} className="btn-outline text-sm" title="Rafraîchir" data-testid="adh-refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
        <button onClick={() => setCreating(true)} className="btn-primary text-sm" data-testid="adh-admin-create">
          <Plus className="w-4 h-4" /> Nouveau membre
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bone text-foreground/70 text-[0.65rem] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Numéro</th>
              <th className="text-left px-4 py-2.5 font-semibold">Nom</th>
              <th className="text-left px-4 py-2.5 font-semibold">Contact</th>
              <th className="text-left px-4 py-2.5 font-semibold">Localisation</th>
              <th className="text-left px-4 py-2.5 font-semibold">Engagement</th>
              <th className="text-left px-4 py-2.5 font-semibold">Statut</th>
              <th className="text-left px-4 py-2.5 font-semibold">Date</th>
              <th className="text-left px-4 py-2.5 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id} className="border-t border-border" data-testid={`adh-row-${a.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs">
                  {a.adhesion_number ? (
                    <span className="bg-ink text-gold px-2 py-1 rounded font-bold tracking-wider">{a.adhesion_number}</span>
                  ) : (
                    <span className="text-foreground/40 italic">—</span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <div className="font-semibold text-ink">{a.first_name} {a.last_name}</div>
                  <div className="text-[0.7rem] text-foreground/50">{a.profession}</div>
                </td>
                <td className="px-4 py-2.5 text-xs">
                  <div>{a.email}</div>
                  <div className="text-foreground/60 mt-0.5">{a.phone}</div>
                </td>
                <td className="px-4 py-2.5 text-xs">
                  <div>{a.city}</div>
                  <div className="text-foreground/60">{a.country}</div>
                </td>
                <td className="px-4 py-2.5">
                  <span className="text-[0.7rem] uppercase tracking-wider bg-gold/20 text-ink px-2 py-0.5 rounded">{ENGAGEMENT[a.engagement_type] || a.engagement_type}</span>
                </td>
                <td className="px-4 py-2.5">
                  <span className={`text-[0.65rem] uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_LABELS[a.status]?.cls}`}>{STATUS_LABELS[a.status]?.label || a.status}</span>
                </td>
                <td className="px-4 py-2.5 text-[0.7rem] text-foreground/60">{fmt(a.submitted_at)}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1.5">
                    {a.status === "pending" && (
                      <>
                        <button onClick={() => approve(a.id)} title="Approuver (numéro auto)"
                                className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-sm" data-testid={`adh-approve-${a.id}`}>
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => setAssigning(a)} title="Attribuer un numéro réservé (FURDC-00001 → 00030)"
                                className="p-1.5 text-ink hover:bg-gold/20 rounded-sm" data-testid={`adh-assign-${a.id}`}>
                          <Hash className="w-4 h-4" />
                        </button>
                        <button onClick={() => setRejecting(a)} title="Refuser"
                                className="p-1.5 text-crimson hover:bg-crimson/10 rounded-sm" data-testid={`adh-reject-${a.id}`}>
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {a.status === "approved" && (
                      <button onClick={() => resendEmail(a.id)} title="Renvoyer l'email de bienvenue"
                              className="p-1.5 text-foreground/60 hover:text-ink rounded-sm" data-testid={`adh-resend-${a.id}`}>
                        <Mail className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => doDelete(a.id)} title="Supprimer"
                            className="p-1.5 text-foreground/40 hover:text-crimson rounded-sm" data-testid={`adh-del-${a.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-foreground/50">Aucune adhésion.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {creating && <CreateModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {assigning && <AssignModal a={assigning} onClose={() => setAssigning(null)} onSaved={() => { setAssigning(null); load(); }} />}
      {rejecting && <RejectModal a={rejecting} onClose={() => setRejecting(null)} onSaved={() => { setRejecting(null); load(); }} />}
    </div>
  );
}

/* ============ SOUS-COMPOSANTS ============ */

function StatCard({ n, l, Icon, tone }) {
  const toneClass = {
    amber: "text-amber-600",
    emerald: "text-emerald-600",
    crimson: "text-crimson",
    ink: "text-ink",
  }[tone] || "text-gold-dark";
  return (
    <div className="bg-white border border-border rounded-sm p-4 flex items-center gap-3">
      <Icon className={`w-6 h-6 ${toneClass}`} />
      <div>
        <div className="font-serif text-2xl text-ink leading-none">{n}</div>
        <div className="text-[0.65rem] uppercase tracking-widest text-foreground/50 mt-1">{l}</div>
      </div>
    </div>
  );
}

function CreateModal({ onClose, onSaved }) {
  const [f, setF] = useState(ADMIN_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.first_name.trim() || !f.last_name.trim() || !f.email.trim()) return toast.error("Nom + Email obligatoires");
    setSaving(true);
    try {
      const payload = { ...f };
      // Le champ adhesion_number est envoyé uniquement s'il est renseigné (format FURDC-NNNNN)
      if (!payload.adhesion_number?.trim()) delete payload.adhesion_number;
      await api.post("/admin/adhesions", payload);
      toast.success("Adhésion créée");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  return (
    <Modal title="Nouveau membre (création directe)" onClose={onClose}>
      <div className="grid sm:grid-cols-2 gap-3">
        <FieldModal label="Prénom *"><input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className="inp" data-testid="adh-c-first" /></FieldModal>
        <FieldModal label="Nom *"><input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className="inp" data-testid="adh-c-last" /></FieldModal>
        <FieldModal label="Email *"><input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="inp" data-testid="adh-c-email" /></FieldModal>
        <FieldModal label="Téléphone *"><input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="inp" data-testid="adh-c-phone" /></FieldModal>
        <FieldModal label="Pays *"><input value={f.country} onChange={(e) => set("country", e.target.value)} className="inp" data-testid="adh-c-country" /></FieldModal>
        <FieldModal label="Ville *"><input value={f.city} onChange={(e) => set("city", e.target.value)} className="inp" data-testid="adh-c-city" /></FieldModal>
        <FieldModal label="Profession *"><input value={f.profession} onChange={(e) => set("profession", e.target.value)} className="inp" data-testid="adh-c-profession" /></FieldModal>
        <FieldModal label="Type">
          <select value={f.engagement_type} onChange={(e) => set("engagement_type", e.target.value)} className="inp bg-white">
            {Object.entries(ENGAGEMENT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </FieldModal>
        <FieldModal label="Numéro forcé (optionnel — pour 00001 à 00030)">
          <input value={f.adhesion_number} onChange={(e) => set("adhesion_number", e.target.value)}
                 className="inp font-mono" placeholder="FURDC-00001" data-testid="adh-c-number" />
        </FieldModal>
        <div className="flex items-center gap-2 mt-6">
          <input type="checkbox" checked={f.auto_approve} onChange={(e) => set("auto_approve", e.target.checked)} data-testid="adh-c-auto" />
          <label className="text-sm">Approuver automatiquement + envoyer email de bienvenue</label>
        </div>
        <FieldModal label="Motivation" full>
          <textarea rows={2} value={f.motivation} onChange={(e) => set("motivation", e.target.value)} className="inp" />
        </FieldModal>
      </div>
      <ModalFooter>
        <button onClick={onClose} className="btn-outline text-sm">Annuler</button>
        <button onClick={submit} disabled={saving} className="btn-primary text-sm" data-testid="adh-c-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Créer
        </button>
      </ModalFooter>
    </Modal>
  );
}

function AssignModal({ a, onClose, onSaved }) {
  const [n, setN] = useState(1);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/adhesions/${a.id}/assign-number`, { number: n });
      toast.success(`Numéro FURDC-${String(n).padStart(5, "0")} attribué`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };
  return (
    <Modal title="Attribuer un numéro réservé" onClose={onClose}>
      <p className="text-sm text-foreground/70 mb-4">
        Attribuez manuellement un numéro <strong className="text-ink">FURDC-00001 à FURDC-00030</strong> à
        <strong className="text-ink"> {a.first_name} {a.last_name}</strong>.
        Ces 30 premiers numéros sont réservés aux membres fondateurs / historiques.
      </p>
      <FieldModal label="Numéro (1-30)">
        <input type="number" min="1" max="30" value={n} onChange={(e) => setN(Math.min(30, Math.max(1, Number(e.target.value) || 1)))}
               className="inp w-32 font-mono text-lg" data-testid="adh-assign-input" />
        <div className="text-xs text-foreground/60 mt-2">Preview : <span className="font-mono font-bold text-ink">FURDC-{String(n).padStart(5, "0")}</span></div>
      </FieldModal>
      <ModalFooter>
        <button onClick={onClose} className="btn-outline text-sm">Annuler</button>
        <button onClick={submit} disabled={saving} className="btn-primary text-sm" data-testid="adh-assign-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Attribuer
        </button>
      </ModalFooter>
    </Modal>
  );
}

function RejectModal({ a, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/adhesions/${a.id}/reject`, { reason });
      toast.success("Adhésion refusée");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };
  return (
    <Modal title="Refuser l'adhésion" onClose={onClose}>
      <p className="text-sm text-foreground/70 mb-4">
        Refuser la demande de <strong className="text-ink">{a.first_name} {a.last_name}</strong>.
      </p>
      <FieldModal label="Motif (optionnel — usage interne)" full>
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="inp" data-testid="adh-reject-reason" />
      </FieldModal>
      <ModalFooter>
        <button onClick={onClose} className="btn-outline text-sm">Annuler</button>
        <button onClick={submit} disabled={saving} className="btn-primary text-sm bg-crimson hover:bg-crimson/90" data-testid="adh-reject-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Refuser
        </button>
      </ModalFooter>
    </Modal>
  );
}

/* Modal helpers */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" />
      <div className="relative bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-serif text-lg text-ink">{title}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-foreground/60" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
function ModalFooter({ children }) {
  return <div className="mt-6 flex items-center justify-end gap-2">{children}</div>;
}
function FieldModal({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
